import { StyleSheet } from 'aphrodite';
import { nodeBorderBlue, titles, white } from '../../../styles/colors';
import { shadows, transitions } from '../styles/baseStyles';
import { FONT_SIZE, HANDLE, NODE_WIDTH, SPACING, TRIGGER_NODE } from '../utils/layoutConstants';

const EXPANDED_TRIGGER = {
	WIDTH: NODE_WIDTH,
	MIN_HEIGHT: 200,
	HEADER_HEIGHT: 56,
} as const;

export const styleSheet = StyleSheet.create({
	wrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},

	// =====================
	// Collapsed state styles (card matching step nodes)
	// =====================
	collapsedCard: {
		width: TRIGGER_NODE.WIDTH,
		minHeight: TRIGGER_NODE.HEIGHT,
		maxHeight: TRIGGER_NODE.HEIGHT,
		borderRadius: 12,
		backgroundColor: white,
		border: `4px solid ${nodeBorderBlue}`,
		boxShadow: shadows.card,
		padding: 20,
		cursor: 'pointer',
		boxSizing: 'border-box',
		overflow: 'hidden',
		transition: transitions.card,
		position: 'relative',
		':hover': {
			boxShadow: shadows.cardHover,
		},
	},
	collapsedCardSelected: {
		boxShadow: `0 0 0 2px ${nodeBorderBlue}, 0px 4px 4px rgba(0, 0, 0, 0.12)`,
	},

	compactHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		minWidth: 0,
	},

	// Expand button as flex item in header row
	expandButton: {
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 8,
		minHeight: 44,
		minWidth: 44,
		color: '#6B7280',
		transition: transitions.background,
		':hover': {
			background: 'rgba(0, 0, 0, 0.05)',
			color: titles,
		},
	},

	// =====================
	// Expanded state styles (card)
	// =====================
	expandedCard: {
		width: EXPANDED_TRIGGER.WIDTH,
		minHeight: EXPANDED_TRIGGER.MIN_HEIGHT,
		backgroundColor: white,
		border: `4px solid ${nodeBorderBlue}`,
		borderRadius: 12,
		boxShadow: shadows.card,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		transition: transitions.card,
		position: 'relative',
	},
	expandedCardSelected: {
		boxShadow: `0 0 0 2px ${nodeBorderBlue}, 0px 4px 4px rgba(0, 0, 0, 0.12)`,
	},

	// Header section of expanded card
	expandedHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: `${SPACING.MD}px ${SPACING.LG}px`,
		borderBottom: '1px solid #F2F2F2',
		minHeight: EXPANDED_TRIGGER.HEADER_HEIGHT,
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACING.MD,
		minWidth: 0,
		flex: 1,
	},
	headerIcon: {
		width: 32,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	headerTitle: {
		flex: 1,
		minWidth: 0,
		fontSize: FONT_SIZE.MD,
		fontWeight: 600,
		color: titles,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},

	// Collapse button
	collapseButton: {
		background: 'transparent',
		border: 'none',
		padding: SPACING.SM,
		cursor: 'pointer',
		borderRadius: 6,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: '#6B7280',
		transition: transitions.background,
		':hover': {
			background: '#F3F4F6',
			color: titles,
		},
	},

	// Content area of expanded card
	expandedContent: {
		padding: `${SPACING.MD}px ${SPACING.LG}px ${SPACING.LG}px`,
		flex: 1,
	},

	// =====================
	// Shared styles
	// =====================
	handle: {
		position: 'absolute',
		bottom: -6,
		left: '50%',
		transform: 'translateX(-50%)',
		width: HANDLE.SIZE,
		height: HANDLE.SIZE,
		background: nodeBorderBlue,
		border: `${HANDLE.BORDER_WIDTH}px solid white`,
		borderRadius: '50%',
	},
	handleExpanded: {
		position: 'absolute',
		bottom: -6,
		left: '50%',
		transform: 'translateX(-50%)',
		width: HANDLE.SIZE,
		height: HANDLE.SIZE,
		background: nodeBorderBlue,
		border: `${HANDLE.BORDER_WIDTH}px solid white`,
		borderRadius: '50%',
	},
	iconContainer: {
		width: 32,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	label: {
		flex: 1,
		minWidth: 0,
		fontSize: FONT_SIZE.MD,
		fontWeight: 600,
		color: titles,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},

	// Read-only badge
	readOnlyBadge: {
		fontSize: 9,
		fontWeight: 600,
		color: '#6B7280',
		backgroundColor: '#F3F4F6',
		padding: '2px 6px',
		borderRadius: 4,
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		marginLeft: SPACING.SM,
	},
});
